import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { CreateTranslationsReq } from 'app/interfaces/requests/Create/createTranslationsReq';
import { ExposedDeleteTranslationsReq } from 'app/interfaces/requests/Exposed/exposedDeleteTranslationsReq';
import { UpdateTranslationsReq } from 'app/interfaces/requests/Update/updateTranslationsReq';
import { ListTranslationConfigsRes } from 'app/interfaces/responses/List/listTranslationConfigsRes';
import { ListTranslationContextsRes } from 'app/interfaces/responses/List/listTranslationContextsRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class TranslationService {
    private httpRequestHelper = new HttpRequestHelper();
    public translationsChanged$ = new Subject<null>();

    constructor(
        private translocoService: TranslocoService,
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog
    ) {}

    public translate(key: string, params: any = null) {
        // Initialize params as an empty object if not provided
        params = !params ? {} : params;
        // Set count value to 1 by default, for some reason the 'other' keyword is the default for pluralization in transloco so translations will always be plural by default
        params.count = params.count == undefined ? 1 : params.count;

        key = key.toLowerCase();

        return this.translocoService.translate(key, params);
    }

    // Utilityu function to get the plural form for a translation (if available)
    // Note: More descriptive than having to define the params as '{ count: 2 }' when calling 'translate'
    public translatePlural(key: string, params: any = null) {
        params = !params ? {} : params;
        params.count = 2;
        return this.translate(key, params);
    }

    public getTranslationConfigs(): Promise<ListTranslationConfigsRes[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translate('info_fetching_information'));
        return new Promise((resolve, reject) => {
            this.http
                .get(environment.localApi + 'Translation/getTranslationConfigs')
                .subscribe({
                    next: (res: HttpStatus<ListTranslationConfigsRes[]>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public listTranslationContexts(showLoader = true): Observable<Array<ListTranslationContextsRes>> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translate('info_fetching_information'));
        }

        let subject = new Subject<Array<ListTranslationContextsRes>>();
        this.http
            .get(environment.localApi + 'Translation/listTranslationContexts', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListTranslationContextsRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    public updateTranslations(req: UpdateTranslationsReq[], showError: boolean = true): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translate('info_update_translations'));

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'Translation/updateTranslations', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translate('info_update_translations_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                error: (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public createTranslation(req: CreateTranslationsReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translate('info_save_data'));

        let subject = new Subject<number>();
        this.http
            .post(environment.localApi + 'Translation/createTranslation', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<number>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translate('info_register_translations_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    this.alertsService.changealertHeader(this.translate('error_register_translations'));
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public deleteTranslation(req: ExposedDeleteTranslationsReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translate('info_save_data'));

        let subject = new Subject<number>();
        this.http
            .delete(
                environment.localApi + `Translation/deleteTranslation/${req.translationId}`,
                this.httpRequestHelper.getRequestOptionsAuth()
            )
            .subscribe(
                (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translate('info_delete_translations_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                (error) => {
                    this.alertsService.changealertHeader(this.translate('error_delete_translations'));
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }
}
